import { AdsClick, Close } from '@mui/icons-material';
import { Chip, Stack, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';

import Autocomplete from 'domains/core/components/Autocomplete';
import { greyChipStyles } from 'domains/core/components/Chip/Chip';
import useGetSendInteractions from 'domains/segments/pages/SegmentCreatePage/Filter/ActivityFilter/ValueAutocomplete/useGetSendInteractions';
import { SegmentFilterEngagementFields, SegmentFilterState, SegmentFilterV2 } from 'domains/segments/types';
import useSendClickableUrls from 'hooks/queries/useSendClickableUrls';

export type Props = {
    activeChannelEngagementFilter?: SegmentFilterV2;
    filter: SegmentFilterState<SegmentFilterEngagementFields>;
    index: number;
    updateSegmentFilter: (
        index: number,
        filter: SegmentFilterState<SegmentFilterEngagementFields>,
        resetPreview?: boolean
    ) => void;
};

const ValueAutocomplete = ({ activeChannelEngagementFilter, filter, index, updateSegmentFilter }: Props) => {
    const supportsMultiSelect = !!activeChannelEngagementFilter;

    const { autocompleteValue: selectedSends, categorySpecificFields } = filter;
    const { sendInteractionField } = categorySpecificFields;

    let interactionAutocompleteValue: any, interactionAutocompleteInputValue: string, isValueAutocompleteVisible;
    if (sendInteractionField) {
        isValueAutocompleteVisible = sendInteractionField.value !== null;
        ({
            autocompleteValue: interactionAutocompleteValue,
            autocompleteInputValue: interactionAutocompleteInputValue,
        } = sendInteractionField);
    }

    const selectedSendValue =
        selectedSends && (Array.isArray(selectedSends) ? selectedSends[0]?.value : selectedSends?.value);
    const sendId = Number(selectedSendValue) || undefined;

    const { data: sendInteractionsData } = useGetSendInteractions({
        path: activeChannelEngagementFilter?.valuesRef,
        reactQueryOptions: { enabled: activeChannelEngagementFilter && !!sendId && isValueAutocompleteVisible },
        sendId,
    });

    const { data: sendClickableUrls } = useSendClickableUrls({
        sendId,
        reactQueryOptions: { enabled: !activeChannelEngagementFilter && !!sendId && isValueAutocompleteVisible },
    });

    const data = sendInteractionsData ?? sendClickableUrls;

    const sendInteractionOptions = data?.map((url: string) => ({
        label: url,
        value: url,
    }));

    const handleInputValueChange = (newInputValue: string) => {
        updateSegmentFilter(index, {
            ...filter,
            categorySpecificFields: {
                ...categorySpecificFields,
                sendInteractionField: {
                    ...sendInteractionField,
                    autocompleteInputValue: newInputValue,
                },
            },
        });
    };

    const handleValueChange = (value: any) => {
        updateSegmentFilter(index, {
            ...filter,
            categorySpecificFields: {
                ...categorySpecificFields,
                sendInteractionField: {
                    ...sendInteractionField,
                    value: value?.value,
                    autocompleteValue: value,
                },
            },
        });
    };

    const handleToggleButtonClick = () => {
        updateSegmentFilter(index, {
            ...filter,
            categorySpecificFields: {
                ...categorySpecificFields,
                sendInteractionField: {
                    autocompleteInputValue: '',
                    autocompleteValue: supportsMultiSelect ? [] : null,
                    value: '',
                },
            },
        });
    };

    const renderAutocomplete = () => {
        const sharedProps = {
            inputValue: interactionAutocompleteInputValue ?? '',
            isOptionEqualToValue: (option: { value: string }, value: { value: string }) => option.value === value.value,
            label: 'Value',
            onChange: (_e: any, value: any) => handleValueChange(value),
            onInputChange: (_e: any, newInputValue: string) => handleInputValueChange(newInputValue),
            options: sendInteractionOptions ?? [],
            sx: { width: '20rem' },
        };

        if (supportsMultiSelect) {
            return (
                <Autocomplete
                    data-testid="select-multi-values-autocomplete"
                    multiple
                    placeholder={!!interactionAutocompleteValue?.length ? '' : 'Enter values'}
                    renderTags={(value, getTagProps) =>
                        value?.map((option, index) => (
                            <Chip
                                deleteIcon={<Close />}
                                label={option.label}
                                {...getTagProps({ index })}
                                sx={greyChipStyles}
                            />
                        ))
                    }
                    type="string"
                    value={interactionAutocompleteValue ?? []}
                    {...sharedProps}
                />
            );
        } else {
            return (
                <Autocomplete
                    data-testid="select-link-autocomplete"
                    data-test="select-link-autocomplete"
                    placeholder="Enter a link"
                    type="string"
                    value={interactionAutocompleteValue ?? null}
                    {...sharedProps}
                />
            );
        }
    };

    return (
        <Stack direction="row" order={supportsMultiSelect ? '4' : '3'} alignItems="center">
            {!isValueAutocompleteVisible ? (
                <>
                    {selectedSends && (
                        <ToggleButtonGroup
                            data-testid="select-link-toggle-button-group"
                            data-test="select-link-toggle-button-group"
                            value="Value"
                            exclusive
                            aria-label="match type"
                            style={{ width: '42px', height: '42px' }}
                        >
                            <ToggleButton
                                data-testid="select-link-toggle-button"
                                data-test="select-link-toggle-button"
                                value="select-link"
                                style={{ width: '42px', height: '42px' }}
                                onClick={handleToggleButtonClick}
                            >
                                <Tooltip aria-label="select value" title="Select Value">
                                    <AdsClick />
                                </Tooltip>
                            </ToggleButton>
                        </ToggleButtonGroup>
                    )}
                </>
            ) : (
                renderAutocomplete()
            )}
        </Stack>
    );
};

export default ValueAutocomplete;
